/**
 * AiSourceLink — Source reference chip + floating tooltip
 *
 * Chip (Figma node 471-15195):
 *   Default bg #F7FAFF, hover #E8F4FC, active #CCE5F2
 *   Border #D9EDF5, radius 5px, text color #0072A9, 11px Open Sans
 *   Shows label + "+N" count badge when there are multiple sources
 *
 * Tooltip (Figma node 469-11718):
 *   White card, 299px wide, radius 10px, border #DDEAEE, shadow 0 2px 8px rgba(0,0,0,0.07)
 *   Head: prev/next chevron navigation + "N sources" label
 *   Body: file icon + filename (bold blue) + external-link icon (opens URL in new tab)
 *         indented excerpt text
 *
 * All colors are hex literals — no theme provider required.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { ThemeContext } from 'styled-components';
import defaultIcons from '../refs/Icons';
import { getRecordFileIcon } from '../utils/recordFileIcon';
import type { RecordClass } from '../utils/recordFileIcon';

// ─── Color tokens ─────────────────────────────────────────────────────────────
// Fallback hex literals — actual values injected via theme when available
const C_CHIP_DEFAULT = '#F7FAFF';
const C_CHIP_HOVER   = '#E8F4FC';
const C_CHIP_ACTIVE  = '#CCE5F2';
const C_CHIP_BORDER  = '#D9EDF5';
const C_CHIP_TEXT    = '#0072A9';

const C_TT_BG        = '#FFFFFF';
const C_TT_BORDER    = '#DDEAEE';
const C_NAV_TEXT     = '#787878';
const C_NAV_ICON     = '#008DD0';
const C_FILE_TEXT    = '#008DD0';
const C_FILE_LINK    = '#0072A9';
const C_EXCERPT_TEXT = '#595959';

type AnyProps = { theme?: { colors?: Record<string, string> } };
const t = (token: string, fallback: string) => (props: AnyProps): string =>
  props.theme?.colors?.[token] ?? fallback;

type AiColors = { main: string; linkColor: string; chipDefault: string; chipHover: string; chipActive: string; chipBorder: string; ttBorder: string };
const useAiColors = (): AiColors => {
  const theme = useContext(ThemeContext) as { colors?: Record<string, string> } | undefined;
  const c = theme?.colors ?? {};
  return {
    main:        c['aiPanelMainColor']    ?? C_NAV_ICON,
    linkColor:   c['aiLinkColor']         ?? C_FILE_LINK,
    chipDefault: c['aiSourceChipDefault'] ?? C_CHIP_DEFAULT,
    chipHover:   c['aiSourceChipHover']   ?? C_CHIP_HOVER,
    chipActive:  c['aiSourceChipActive']  ?? C_CHIP_ACTIVE,
    chipBorder:  c['aiSourceChipBorder']  ?? C_CHIP_BORDER,
    ttBorder:    c['aiTooltipBorder']     ?? C_TT_BORDER,
  };
};

// ─── Types ────────────────────────────────────────────────────────────────────
export type AiSourceEntry = {
  /** Display name of the source file */
  filename: string;
  /** Quoted excerpt from the source */
  excerpt: string;
  /** Optional URL — clicking the external-link icon opens this in a new tab */
  url?: string;
  /** Optional image URL to display in the tooltip (e.g. a blob URL from a fetched picture) */
  imageUrl?: string;
};

export type AiSourceLinkProps = {
  /** Short label shown on the chip (e.g. "Med log") */
  label: string;
  /** One or more source excerpts from this file */
  sources: AiSourceEntry[];
  /** Record class used to resolve the file icon (e.g. 'Pathology', 'Radiology') */
  recordClass?: RecordClass;
  /** Explicit file extension override for icon resolution */
  fileExtension?: string;
  /** Callback fired when the open-external icon is clicked. Receives the current source entry. */
  onOpen?: (source: AiSourceEntry) => void;
  className?: string;
};

// ─── Chip ─────────────────────────────────────────────────────────────────────
const ChipButton = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px 2px 8px;
  background: ${(props: AnyProps & { $open: boolean }) => (props.$open ? t('aiSourceChipActive', C_CHIP_ACTIVE)(props) : t('aiSourceChipDefault', C_CHIP_DEFAULT)(props))};
  border: 1px solid ${t('aiSourceChipBorder', C_CHIP_BORDER)};
  border-radius: 5px;
  cursor: pointer;
  font-family: 'Open Sans', sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: ${t('aiLinkColor', C_CHIP_TEXT)};
  line-height: normal;
  transition: background 0.1s;
  &:hover { background: ${(props: AnyProps & { $open: boolean }) => (props.$open ? t('aiSourceChipActive', C_CHIP_ACTIVE)(props) : t('aiSourceChipHover', C_CHIP_HOVER)(props))}; }
  &:active { background: ${t('aiSourceChipActive', C_CHIP_ACTIVE)}; }
`;

const ChipCount = styled.span`
  font-size: 11px;
  color: ${t('aiLinkColor', C_CHIP_TEXT)};
`;

// ─── Tooltip popup ────────────────────────────────────────────────────────────
const TooltipRoot = styled.div<{ $visible: boolean }>`
  position: fixed;
  z-index: 99999;
  width: 299px;
  background: ${C_TT_BG};
  border: 1px solid ${t('aiTooltipBorder', C_TT_BORDER)};
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  font-family: 'Open Sans', sans-serif;
  overflow: hidden;
  pointer-events: all;
  opacity: ${(props: { $visible: boolean }) => (props.$visible ? 1 : 0)};
  transition: opacity 300ms ease;
`;

const TooltipHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 8px 8px;
  background: ${C_TT_BG};
  border-bottom: 1px solid ${t('aiTooltipBorder', C_TT_BORDER)};
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  &:disabled { opacity: 0.3; cursor: default; }
`;

const NavText = styled.span`
  font-size: 12px;
  color: ${C_NAV_TEXT};
`;

const SourceCountLabel = styled.span`
  font-size: 11px;
  color: ${C_NAV_TEXT};
`;

const TooltipBody = styled.div`
  padding: 16px 11px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${t('aiPanelMainColor', C_FILE_TEXT)};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExternalLinkAnchor = styled.a`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${t('aiLinkColor', C_FILE_LINK)};
  text-decoration: none;
  cursor: pointer;
  opacity: 0.9;
  border: none;
  background: none;
  padding: 0;
  &:hover { opacity: 1; }
`;

const ExcerptWrap = styled.div`
  padding-left: 24px;
  font-size: 12px;
  font-weight: 400;
  color: ${C_EXCERPT_TEXT};
  line-height: 1.55;
  max-height: 108px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
`;

const SourceImageEl = styled.img`
  max-width: 100%;
  max-height: 180px;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,0.08);
  margin-top: 6px;
  margin-left: 24px;
  display: block;
`;

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
const ChevronLeftSvg = ({ color }: { color: string }) => (
  <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
    <path d="M5 1L1 5l4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightSvg = ({ color }: { color: string }) => (
  <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
    <path d="M1 1l4 4-4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FileIconSvg = ({ color }: { color: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 2.5A1.5 1.5 0 0 1 4.5 1H9l4 4v8.5A1.5 1.5 0 0 1 11.5 15h-7A1.5 1.5 0 0 1 3 13.5V2.5Z"
      stroke={color} strokeWidth="1.2" fill="none" />
    <path d="M9 1v3.5H13" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RecordFileIconSpan = styled.span`
  font-family: 'purview-icons-v2' !important;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  font-size: 16px;
  color: ${t('aiPanelMainColor', C_FILE_TEXT)};
  display: inline-flex;
  align-items: center;
`;

const RecordFileIcon: React.FC<{ filename: string; recordClass?: RecordClass; fileExtension?: string; color: string }> = ({ filename, recordClass, fileExtension, color }) => {
  const iconName = getRecordFileIcon(filename, recordClass, fileExtension);
  const glyph = defaultIcons[iconName as keyof typeof defaultIcons];
  if (glyph) {
    return <RecordFileIconSpan>{glyph}</RecordFileIconSpan>;
  }
  return <FileIconSvg color={color} />;
};

const ExternalLinkIconSvg = ({ color }: { color: string }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M5 2H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V7"
      stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 1h3v3M11 1 6 6"
      stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
export const AiSourceLink: React.FC<AiSourceLinkProps> = ({ label, sources, recordClass, fileExtension, onOpen, className }) => {
  const aiC = useAiColors();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const total = sources.length;

  // Position the tooltip above the chip, aligned to its left edge
  const computePosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const ttWidth = 299;
    const ttEstimatedHeight = 170; // rough estimate; actual measured after mount
    const gap = 8;

    let left = rect.left;
    // prevent overflow on the right
    if (left + ttWidth > window.innerWidth - 8) {
      left = window.innerWidth - ttWidth - 8;
    }
    // default: above chip
    let top = rect.top - ttEstimatedHeight - gap;
    if (top < 8) {
      // flip below
      top = rect.bottom + gap;
    }
    setTooltipStyle({ top, left });
  }, []);

  // Recompute after mount so we can use actual tooltip height
  const [tooltipMounted, setTooltipMounted] = useState(false);
  const refinePosition = useCallback((node: HTMLDivElement | null) => {
    (tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    setTooltipMounted(!!node);
    if (!node || !wrapperRef.current) return;
    const chipRect = wrapperRef.current.getBoundingClientRect();
    const ttRect = node.getBoundingClientRect();
    const gap = 8;

    let left = chipRect.left;
    if (left + ttRect.width > window.innerWidth - 8) {
      left = window.innerWidth - ttRect.width - 8;
    }
    let top = chipRect.top - ttRect.height - gap;
    if (top < 8) {
      top = chipRect.bottom + gap;
    }
    setTooltipStyle({ top, left });
  }, []);

  const closeTooltip = useCallback(() => {
    setVisible(false);
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = setTimeout(() => setOpen(false), 300);
  }, []);

  const openTooltip = useCallback(() => {
    if (fadeTimeoutRef.current) { clearTimeout(fadeTimeoutRef.current); fadeTimeoutRef.current = null; }
    setIndex(0);
    computePosition();
    setOpen(true);
    // Show on next frame so transition triggers
    requestAnimationFrame(() => setVisible(true));
  }, [computePosition]);

  const handleChipClick = () => {
    if (open) {
      closeTooltip();
    } else {
      openTooltip();
    }
  };

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideChip = wrapperRef.current?.contains(target);
      const insideTooltip = tooltipRef.current?.contains(target);
      if (!insideChip && !insideTooltip) {
        closeTooltip();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, closeTooltip]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeTooltip(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeTooltip]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    };
  }, []);

  const current = sources[Math.min(index, total - 1)];

  const tooltip = open && current
    ? ReactDOM.createPortal(
        <TooltipRoot ref={refinePosition} style={tooltipStyle} $visible={visible}>
          {total > 1 && (
            <TooltipHead>
              <NavRow>
                <NavButton
                  type="button"
                  aria-label="Previous source"
                  disabled={index === 0}
                  onClick={() => setIndex(i => Math.max(0, i - 1))}
                >
                  <ChevronLeftSvg color={aiC.main} />
                </NavButton>
                <NavText>{index + 1} / {total}</NavText>
                <NavButton
                  type="button"
                  aria-label="Next source"
                  disabled={index === total - 1}
                  onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
                >
                  <ChevronRightSvg color={aiC.main} />
                </NavButton>
              </NavRow>
              <SourceCountLabel>{total} source{total !== 1 ? 's' : ''}</SourceCountLabel>
            </TooltipHead>
          )}
          <TooltipBody>
            <FileRow>
              <RecordFileIcon filename={current.filename} recordClass={recordClass} fileExtension={fileExtension} color={aiC.main} />
              <FileName title={current.filename}>{current.filename}</FileName>
              {(current.url || onOpen) && (
                <ExternalLinkAnchor
                  as={onOpen ? 'button' : 'a'}
                  type={onOpen ? 'button' : undefined}
                  href={onOpen ? undefined : current.url}
                  target={onOpen ? undefined : '_blank'}
                  rel={onOpen ? undefined : 'noopener noreferrer'}
                  aria-label={`Open ${current.filename} in new tab`}
                  title="Open in new tab"
                  onClick={onOpen ? (e: React.MouseEvent) => { e.preventDefault(); onOpen(current); } : undefined}
                >
                  <ExternalLinkIconSvg color={aiC.linkColor} />
                </ExternalLinkAnchor>
              )}
            </FileRow>
            <ExcerptWrap>{current.excerpt}</ExcerptWrap>
            {current.imageUrl && <SourceImageEl src={current.imageUrl} alt={current.excerpt || 'Source image'} />}
          </TooltipBody>
        </TooltipRoot>,
        document.body
      )
    : null;

  return (
    <span ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <ChipButton
        type="button"
        $open={open}
        className={className}
        onClick={handleChipClick}
        aria-label={`${label} source${total !== 1 ? 's' : ''}: ${total}`}
        aria-expanded={open}
      >
        {label}
        {total > 1 && <ChipCount>+{total-1}</ChipCount>}
      </ChipButton>
      {tooltip}
    </span>
  );
};

export default AiSourceLink;
